import { useMutation, useQueryClient } from "@tanstack/react-query";
import { fetch } from "@/src/lib/fetch";
import { Amenity, AmenityDetails, Booking } from "../app/Bookings/model";
import { useBookingData } from "./useBookingData";
import { useToast } from "../components/ui/use-toast";
import { useBookingsStore } from "../stores/bookingsStore";

export const useBookingExtrasMutation = (booking: Booking) => {
  const { fetchAmenitiesDetails, fetchAmenities } = useBookingData(booking);
  const { setBooking, booking: bookingData } = useBookingsStore();

  const toast = useToast();
  const queryClient = useQueryClient();

  const { mutate: updateExtras } = useMutation<Response, unknown, AmenityDetails[], unknown>({
    mutationFn: (data) =>
    {
      return fetch<any>(
        `/api/bookings/amenities-details/${bookingData?.id}`,
        "PUT",
        data,
      ).then((res) => res)
    },
    onSuccess: () => {
      toast.toast({
        description: "Extras saved",
        variant: "default",
        duration: 1000,
      });
      fetchAmenities().then((res) => {
        if (!!res.data?.length && bookingData) {
          setBooking({
            ...booking,
            offerYacht: {
              ...bookingData?.offerYacht,
              yacht: {
                ...bookingData?.offerYacht?.yacht,
                amenities: res.data,
              },
            },
          });
        }
      });
      fetchAmenitiesDetails();

      queryClient.invalidateQueries({ queryKey: ["bookingYachtOfferData", booking.id] });
    },
    onError: (error) => {
      console.log(error);
      toast.toast({
        description: "Error saving extras",
        variant: "destructive",
        duration: 1000,
      });
    },
  });
  const { mutateAsync: createExtra, data } = useMutation<
    { data: Amenity[] },
    unknown,
    (Amenity & {
      dedupeKey: string;
      currencyId: string;
      offerYachtId: string;
      currencyCode: string;
      isCustomAmenity: boolean;
      finalBookingPrice: number;
      checked: boolean;
      yachtId?: string | null;
      yachtIndex?: number; 
    })[],
    unknown
  >({
    mutationFn: (data) => {
      console.log('dataAA', data);
      return fetch<{ data: Amenity[] }>(
        `/api/amenities/booking?updateBooking=${true}&bookingId=${bookingData?.id}`,
        "POST",
        data,
      ).then((res) => res)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bookingYachtOfferData", bookingData?.id] });

      toast.toast({
        description: "New extra saved",
        variant: "default",
        duration: 1000,
      });
      fetchAmenities();
      fetchAmenitiesDetails();
    },
    onError: (error) => {
      console.log(error);
      toast.toast({
        description: "Error saving new extra",
        variant: "destructive",
        duration: 1000,
      });
    },
  });

  const { mutateAsync: removeAmenity } = useMutation<{ id: string }, unknown, { id: string, bookingId: string }, unknown>({
    mutationFn: ({ id, bookingId }) => fetch<{ id: string, bookingId: string }>(`/api/bookings/${bookingId}/amenities/${id}`, "DELETE").then((res) => res),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ["bookingYachtOfferData", bookingData?.id] });
      queryClient.invalidateQueries({ queryKey: ["amenitiesDetailsData", bookingData?.offerYacht?.id] });

      toast.toast({
        description: "Amenity removed",
        variant: "default",
        duration: 1000,
      });
      fetchAmenities();
      fetchAmenitiesDetails();
      bookingData &&
        setBooking({
          ...booking,
          offerYacht: {
            ...bookingData?.offerYacht,
            yacht: {
              ...bookingData?.offerYacht?.yacht,
              amenities: bookingData?.offerYacht?.yacht?.amenities?.filter((am) => am.id !== res.data?.id),
            },
          },
        });
    },
    onError: (error) => {
      console.log(error);
      toast.toast({
        description: "Error removing amenity",
        variant: "destructive",
        duration: 1000,
      });
    },
  });

  return {
    updateExtras,
    createExtra,
    removeAmenity,
    newExtra: data,
  };
};
